import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { Fragment } from "react";
import { Typography } from "@/components/atoms";
import { clsx } from "@/utils";

export interface ModalProps extends React.HTMLAttributes<HTMLDivElement> {
  open: boolean;
  title?: string;
  description?: string;
  onClose?: () => void;
  renderButtons?: () => JSX.Element;
  featuredIcon?: JSX.Element;
  variant?: "web-small" | "web-medium" | "web-large" | "mobile";
  headerClassName?: string;
}

export const Modal = ({
  open,
  title,
  description,
  children,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onClose = () => {},
  renderButtons,
  featuredIcon,
  variant = "web-medium",
  className,
  headerClassName,
}: ModalProps) => {
  const getVariantClasses = () => {
    switch (variant) {
      case "web-large":
        return "w-full max-w-4xl";
      case "web-medium":
        return "w-full max-w-2xl";
      case "mobile":
        return "w-full max-w-sm";
      case "web-small":
      default:
        return "w-full max-w-lg";
    }
  };

  const renderFeaturedIcon = () => {
    if (!featuredIcon) return null;

    return featuredIcon;
  };

  const renderButtonContainer = () => {
    if (!renderButtons) return null;

    if (variant === "mobile") {
      return <div className="flex w-full flex-col gap-3">{renderButtons()}</div>;
    }

    return <div className="flex w-full justify-end gap-3">{renderButtons()}</div>;
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-neutral-mid-gray bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-3">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                className={clsx(
                  "relative transform overflow-hidden rounded-xl bg-white p-6 text-left shadow-lg transition-all",
                  getVariantClasses(),
                  className,
                )}
              >
                <div className={clsx("flex flex-col gap-8", variant === "mobile" ? "items-center" : "items-end")}>
                  <div className="flex w-full flex-col gap-5">
                    {renderFeaturedIcon()}

                    {(title || description) && (
                      <div className={clsx("flex w-full flex-col gap-2", headerClassName)}>
                        {title && <Typography variant="title">{title}</Typography>}
                        {description && (
                          <Typography variant="paragraph" className="text-neutral-dark-gray">
                            {description}
                          </Typography>
                        )}
                      </div>
                    )}

                    {children && <div className="w-full">{children}</div>}
                  </div>

                  {renderButtonContainer()}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
